import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import Icon from "../../../components/AppIcon";
import { getAuthData } from "./../../../utils/auth";
import apiService from "services/api";
import { useAuth } from "../../../context/AuthContext";
import GoogleLoginButton from "../../../components/auth/GoogleLoginButton";

const LoginForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  // Check if already logged in
  useEffect(() => {
    const { isAuthenticated, token } = getAuthData();
    if (isAuthenticated && token) {
      navigate("/dashboard");
    }
  }, [navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // Updated login function with real API integration
  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      // Real API call to backend
      const result = await apiService.login(data);

      if (result.success) {
        // Use the AuthContext login function to properly update the auth state
        login(result.data.user, result.data.token);
        console.log("Login successful:", result.data.user);
        navigate("/dashboard");
      } else {
        // Handle API errors
        if (result.message.includes("email")) {
          setError("email", {
            type: "manual",
            message: result.message,
          });
        } else if (result.message.includes("password")) {
          setError("password", {
            type: "manual",
            message: result.message,
          });
        } else if (result.message.includes("verify") || result.message.includes("verification")) {
          setError("root", {
            type: "manual",
            message: result.message + " Please check your email for the verification link.",
          });
        } else {
          setError("root", {
            type: "manual",
            message: result.message,
          });
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      setError("root", {
        type: "manual",
        message: "Login failed. Please check your connection and try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleGoogleLogin = async (user, token) => {
    // Use the AuthContext login function to properly update the auth state
    login(user, token);
    console.log("Google login successful:", user);
    navigate("/dashboard");
  };

  const handleGoogleLoginError = (errorMessage) => {
    console.error("Google login error:", errorMessage);
    setError("root", {
      type: "manual",
      message: errorMessage || "Google login failed. Please try again.",
    });
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Email Input */}
        <div>
          <Input
            label="Email Address"
            type="email"
            placeholder="Enter your email"
            required
            error={errors?.email?.message}
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Please enter a valid email address",
              },
            })}
          />
        </div>

        {/* Password Input */}
        <div className="relative">
          <Input
            label="Password"
            type={showPassword ? "text" : "password"}
            placeholder="Enter your password"
            required
            error={errors?.password?.message}
            {...register("password", {
              required: "Password is required",
              minLength: {
                value: 6,
                message: "Password must be at least 6 characters",
              },
            })}
          />
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-micro focus:outline-none focus:text-foreground"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            <Icon name={showPassword ? "EyeOff" : "Eye"} size={20} />
          </button>
        </div>

        {/* Forgot Password Link */}
        <div className="text-right">
          <Link
            to="/forgot-password"
            className="text-sm text-primary hover:text-primary/80 transition-micro focus:outline-none focus:underline"
          >
            Forgot your password?
          </Link>
        </div>

        {/* Sign In Button */}
        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isLoading}
          disabled={isLoading}
        >
          {isLoading ? "Signing In..." : "Sign In"}
        </Button>

        {/* Google Login Button */}
        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border"></div>
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
          </div>
        </div>

        <GoogleLoginButton
          onGoogleLogin={handleGoogleLogin}
          onError={handleGoogleLoginError}
        />

        {/* Create Account Link */}
        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            Don't have an account?{" "}
            <Link
              to="/register"
              className="text-primary hover:text-primary/80 font-medium transition-micro focus:outline-none focus:underline"
            >
              Create Account
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;
